import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
  UserPlus, Search, FileText, Send, CheckCircle, GraduationCap,
  ArrowRight, Sparkles, Shield, Clock, Users, MessageSquare
} from 'lucide-react';

const HowItWorks = () => {
  const steps = [
    {
      number: 1,
      icon: UserPlus,
      title: "Create Your Account",
      description: "Sign up for free and build your student profile with your academic history, extracurriculars, and personal information.",
      details: [
        "Complete your profile once, use it everywhere",
        "Add test scores, transcripts, and documents",
        "Build your activities and achievements list"
      ]
    },
    {
      number: 2,
      icon: Search,
      title: "Explore Colleges",
      description: "Browse our database of colleges and universities. Filter by location, major, acceptance rate, and more.",
      details: [
        "Use our Chance Me tool to see your odds",
        "Compare colleges side by side",
        "Save favorites to your list"
      ]
    },
    {
      number: 3,
      icon: FileText,
      title: "Build Your Applications",
      description: "Complete college-specific supplements and essays. Our platform pre-fills common information across all applications.",
      details: [
        "One Common Application for multiple schools",
        "Essay assistance and guidelines",
        "Track requirements and deadlines"
      ]
    },
    {
      number: 4,
      icon: Users,
      title: "Connect with Counselors",
      description: "Your school counselor can review applications, submit transcripts, and provide recommendations—all through Wayo.",
      details: [
        "Request recommendations easily",
        "Counselors can track your progress",
        "Secure document sharing"
      ]
    },
    {
      number: 5,
      icon: Send,
      title: "Submit Applications",
      description: "Review your completed applications and submit them directly to colleges with a single click.",
      details: [
        "Pay application fees securely",
        "Track submission status in real-time",
        "Receive confirmation from colleges"
      ]
    },
    {
      number: 6,
      icon: CheckCircle,
      title: "Track Decisions",
      description: "Monitor your application status and receive admission decisions all in one place.",
      details: [
        "Real-time status updates",
        "Decision notifications",
        "Financial aid information"
      ]
    }
  ];

  const features = [
    {
      icon: Sparkles,
      title: "AI-Powered Tools",
      description: "Get personalized college recommendations and application assistance powered by AI."
    },
    {
      icon: Shield,
      title: "Secure & Private",
      description: "Your data is encrypted and never shared without your consent."
    },
    {
      icon: Clock,
      title: "Save Time",
      description: "Apply to multiple colleges with one application—no redundant data entry."
    },
    {
      icon: MessageSquare,
      title: "24/7 Support",
      description: "Get help anytime with our AI assistant or support team."
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-emerald-600 to-teal-600 text-white py-20">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <h1 className="text-4xl sm:text-5xl font-bold mb-6">How Wayo Works</h1>
          <p className="text-lg sm:text-xl max-w-3xl mx-auto opacity-90">
            Your journey to college starts here. We've simplified the entire application process 
            into six easy steps.
          </p>
        </div>
      </div>

      {/* Steps Section */}
      <div className="max-w-5xl mx-auto px-4 py-20">
        <div className="space-y-12">
          {steps.map((step, index) => (
            <div key={index} className="flex gap-6">
              {/* Step Number & Icon */}
              <div className="flex flex-col items-center">
                <div className="w-14 h-14 bg-gradient-to-r from-emerald-500 to-teal-500 text-white rounded-full flex items-center justify-center font-bold text-xl shadow-lg">
                  {step.number}
                </div>
                {index < steps.length - 1 && (
                  <div className="w-0.5 h-full bg-emerald-200 mt-4" />
                )}
              </div>

              {/* Step Content */}
              <Card className="flex-1 hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <step.icon className="w-5 h-5 text-emerald-600" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-xl font-semibold mb-2">{step.title}</h3>
                      <p className="text-gray-600 mb-4">{step.description}</p>
                      <ul className="space-y-2">
                        {step.details.map((detail, i) => (
                          <li key={i} className="flex items-center gap-2 text-sm text-gray-600">
                            <CheckCircle className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                            {detail}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          ))}
        </div>
      </div>

      {/* Features Section */}
      <div className="bg-gray-50 py-20">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">Why Students Love Wayo</h2>
            <p className="text-gray-600">Trusted by over 1 million students worldwide</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <Card key={index} className="text-center">
                <CardContent className="p-6">
                  <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <feature.icon className="w-6 h-6 text-emerald-600" />
                  </div>
                  <h3 className="font-semibold mb-2">{feature.title}</h3>
                  <p className="text-gray-600 text-sm">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </div>

      {/* For Different Users */}
      <div className="max-w-6xl mx-auto px-4 py-20">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold mb-4">Built for Everyone</h2>
        </div>
        <div className="grid md:grid-cols-3 gap-8">
          <Card className="border-t-4 border-t-emerald-500">
            <CardContent className="p-6">
              <h3 className="text-xl font-semibold mb-4 flex items-center gap-2">
                <GraduationCap className="w-5 h-5 text-emerald-600" />
                Students
              </h3>
              <p className="text-gray-600 mb-4">
                Apply to multiple colleges, track your applications, and get AI-powered guidance throughout the process.
              </p>
              <Link to="/signup">
                <Button variant="outline" className="w-full">
                  Create Student Account
                </Button>
              </Link>
            </CardContent>
          </Card>

          <Card className="border-t-4 border-t-blue-500">
            <CardContent className="p-6">
              <h3 className="text-xl font-semibold mb-4 flex items-center gap-2">
                <Users className="w-5 h-5 text-blue-600" />
                Counselors
              </h3>
              <p className="text-gray-600 mb-4">
                Support your students, manage recommendations, and submit official documents all in one place.
              </p>
              <Link to="/counselor">
                <Button variant="outline" className="w-full">
                  Counselor Portal
                </Button>
              </Link>
            </CardContent>
          </Card>

          <Card className="border-t-4 border-t-purple-500">
            <CardContent className="p-6">
              <h3 className="text-xl font-semibold mb-4 flex items-center gap-2">
                <Users className="w-5 h-5 text-purple-600" />
                Parents
              </h3>
              <p className="text-gray-600 mb-4">
                Stay informed about your student's application progress, manage payments, and review financial aid.
              </p>
              <Link to="/parent">
                <Button variant="outline" className="w-full">
                  Parent Portal
                </Button>
              </Link>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* CTA Section */}
      <div className="bg-gradient-to-r from-emerald-600 to-teal-600 text-white py-16">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-4">Ready to Start Your Journey?</h2>
          <p className="text-lg opacity-90 mb-8">
            Join over 1 million students who have found their way forward with Wayo.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/signup">
              <Button size="lg" className="bg-white text-emerald-600 hover:bg-gray-100 px-8">
                Get Started Free
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
            <Link to="/f1-guide">
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10 px-8">
                F-1 Visa Guide
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HowItWorks;
